/**
 * Category requirements utilities
 * Helpers to extract and organize requirements from API category data
 */

import type { MembershipCategory } from "@/utils/membership/types"
import {
  normaliseRequirement,
  inferRequirementStep,
  isDocumentRequirement,
  type ResolvedRequirement,
} from "@/utils/membership/requirements"
import type { MembershipApplicationStepId } from "../types"
import type { StepRequirementSummary } from "@/utils/membership/types"


/**
 * Convert a Requirement to StepRequirementSummary
 */
function toStepRequirementSummary(requirement: ResolvedRequirement): StepRequirementSummary {
  return {
    id: requirement.id,
    label: requirement.label,
    mandatory: requirement.mandatory,
    type: requirement.type,
    description: requirement.description,
    acceptedFormats: requirement.metadata?.acceptedFormats as string[] | undefined,
  }
}

/**
 * Get requirements for a specific category and step from API data
 */
export function getCategoryRequirements(
  category: MembershipCategory | undefined,
  stepId: MembershipApplicationStepId,
): StepRequirementSummary[] {
  if (!category?.requirements || !Array.isArray(category.requirements)) {
    return []
  }

  const resolved = category.requirements
    .map((req) => normaliseRequirement(req))
    .filter((req): req is ResolvedRequirement => req !== undefined)

  // Filter requirements for the specific step
  const stepRequirements = resolved.filter((req) => {
    const reqStep = inferRequirementStep(req, "profile")
    return reqStep === stepId
  })

  return stepRequirements.map(toStepRequirementSummary)
}

/**
 * Get document requirements for a category
 */
export function getCategoryDocumentRequirements(
  category: MembershipCategory | undefined,
): StepRequirementSummary[] {
  if (!category?.requirements || !Array.isArray(category.requirements)) {
    return []
  }

  const resolved = category.requirements
    .map((req) => normaliseRequirement(req))
    .filter((req): req is ResolvedRequirement => req !== undefined)

  // Filter only document (FILE) requirements
  const documentRequirements = resolved.filter(isDocumentRequirement)

  return documentRequirements.map(toStepRequirementSummary)
}

/**
 * Get non-document requirements for a category (for the requirements step)
 */
export function getCategoryNonDocumentRequirements(
  category: MembershipCategory | undefined,
): StepRequirementSummary[] {
  if (!category?.requirements || !Array.isArray(category.requirements)) {
    return []
  }

  const resolved = category.requirements
    .map((req) => normaliseRequirement(req))
    .filter((req): req is ResolvedRequirement => req !== undefined)

  // Filter out FILE requirements - those go in documents step
  const nonDocumentRequirements = resolved.filter((req) => !isDocumentRequirement(req))

  return nonDocumentRequirements.map(toStepRequirementSummary)
}

/**
 * Get all requirements for a category, organized by step
 */
export function getCategoryRequirementsByStep(
  category: MembershipCategory | undefined,
): Record<MembershipApplicationStepId, StepRequirementSummary[]> {
  const result: Record<string, StepRequirementSummary[]> = {
    profile: [],
    requirements: [],
    employment: [],
    education: [],
    affiliations: [],
    documents: [],
    review: [],
  }

  if (!category?.requirements || !Array.isArray(category.requirements)) {
    return result as Record<MembershipApplicationStepId, StepRequirementSummary[]>
  }

  const resolved = category.requirements
    .map((req) => normaliseRequirement(req))
    .filter((req): req is ResolvedRequirement => req !== undefined)

  for (const req of resolved) {
    const stepId = inferRequirementStep(req, "profile")
    const summary = toStepRequirementSummary(req)

    if (result[stepId]) {
      result[stepId].push(summary)
    }
  }

  return result as Record<MembershipApplicationStepId, StepRequirementSummary[]>
}

/**
 * Check if a category has any non-document requirements
 */
export function categoryHasNonDocumentRequirements(
  category: MembershipCategory | undefined,
): boolean {
  const nonDocReqs = getCategoryNonDocumentRequirements(category)
  return nonDocReqs.length > 0
}
